import getResponseArray from '@tymbe/utils/getResponseArray';
import moment, { Moment } from 'moment';
import { useQuery } from 'react-query';
import apiClient from '../../apiClient';
import { FormApi } from 'informed';
import { CompanyData } from '@tymbe/schema/company.interface';
import { AdminPopup, AdminPopupDisplayFrequencyDaysEnum, AdminPopupImageEnum } from '@tymbe/schema/admin-popup.interface';

export const adminPopupFormDefaultValues: AdminPopupFormValues = {
  from: null,
  to: null,
  name: '',
  display_frequency: AdminPopupDisplayFrequencyDaysEnum.once,
  display_frequency_weekday: null,
  image: AdminPopupImageEnum.thumbsUp,
  button_text: null,
  button_url: null,
  content: '',
  company_ids: [],
  show_on_modul_page: [],
};

export type AdminPopupFormValues = {
  id?: number;
  name: string;
  from: Moment | null;
  to: Moment | null;
  display_frequency: AdminPopupDisplayFrequencyDaysEnum;
  image: AdminPopupImageEnum;
  button_text: string | null;
  button_url: string | null;
  content: string;
  display_frequency_weekday: { value: number; label: string }[] | null;
  company_ids: { value: number; label: string }[];
  show_on_modul_page: { value: string; label: string }[];
};

export const createAdminPopupWeekdayOptions = [
  { value: 1, label: 'Pondělí' },
  { value: 2, label: 'Úterý' },
  { value: 3, label: 'Středa' },
  { value: 4, label: 'Čtvrtek' },
  { value: 5, label: 'Pátek' },
  { value: 6, label: 'Sobota' },
  { value: 0, label: 'Neděle' },
];

export const adminPopupModuleOptions = [
  { value: 'calendar', label: 'Kalendář' },
  { value: 'shift', label: 'Objednávky' },
  { value: 'attendance', label: 'Docházka' },
  { value: 'tymber', label: 'Tymbeři' },
  { value: 'company', label: 'Firma' },
  { value: 'blocks', label: 'Blokace' },
];

export function useCompaniesWithBranchoffices() {
  return useQuery({
    queryKey: ['company', 'branchoffice', 'department', 'list'],
    queryFn: async () => {
      const companies = getResponseArray(
        await apiClient.service('company').find({
          query: {
            $limit: -1,
            state: 'active',
          },
        })
      );
      return companies;
    },
    refetchOnWindowFocus: false,
  });
}

export function mapLabelValueToValues<T>(labelValues: { value: T; label: string }[]): T[] {
  return labelValues.map((v: { value: T; label: string }) => v.value);
}

export function validateFromBeforeTo(
  value: Moment | null,
  formApiRef: React.RefObject<FormApi<AdminPopupFormValues> | undefined>
) {
  const toValue = formApiRef.current?.getValue('to');
  if (value && toValue && value.isAfter(toValue, 'day')) {
    return 'Datum "Aktivní od" musí být před "Aktivní do"';
  }
  return undefined;
}

export function getSortedCompanyOptions(companies: CompanyData[]) {
  return companies
    .sort((a, b) => a.name.localeCompare(b.name))
    .sort((a, b) => a.id - b.id)
    .map((company) => ({ value: company.id, label: `[${company.id}] ${company.name}` }));
}

export function getFilteredWeekdayOptions(from: moment.Moment | null, to: moment.Moment | null) {
  if (!from || !to) {
    return createAdminPopupWeekdayOptions;
  }

  if (from.isAfter(to)) {
    return createAdminPopupWeekdayOptions;
  }

  const startDate = from.clone().startOf('day');
  const endDate = to.clone().endOf('day');

  const availableWeekdays = new Set<number>();
  const currentDate = startDate.clone();

  const daysOfTheWeek = 7;

  while (currentDate.isSameOrBefore(endDate) && availableWeekdays.size !== daysOfTheWeek) {
    const weekday = currentDate.day();
    availableWeekdays.add(weekday);
    currentDate.add(1, 'day');
  }

  return createAdminPopupWeekdayOptions.filter((day) => availableWeekdays.has(day.value));
}

export interface UseAdminPopupOptions {
  module: 'tymber' | 'attendance' | 'shift' | 'calendar' | 'company' | 'blocks';
}

export function getPopupsForModule(popups: AdminPopup[], module: UseAdminPopupOptions['module']): AdminPopup[] {
  return popups.filter(
    (popup: AdminPopup) =>
      popup.show_on_modul_page?.includes(module) || (popup.show_on_modul_page?.length === 0 && module === 'calendar')
  );
}

export function getPopupsForCompanyIds(popups: AdminPopup[], userCompanyIds: number[]): AdminPopup[] {
  return popups.filter(
    (popup: AdminPopup) =>
      popup.company_ids?.some((id: number) => userCompanyIds.includes(id)) || popup.company_ids?.length === 0
  );
}

export function isNowWithinPopupTimeInterval(popup: AdminPopup): boolean {
  const now = moment();
  return now.isAfter(moment(popup.from)) && (popup.to ? now.isBefore(moment(popup.to)) : true);
}
